import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { Slot } from "radix-ui";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  "relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl font-medium transition-[background,color,box-shadow,transform,opacity] duration-150 active:scale-[.98] disabled:pointer-events-none disabled:opacity-45 focus-visible:outline-none focus-visible:shadow-[var(--ring)] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-accent-solid text-on-accent shadow-xs hover:brightness-110",
        brand: "text-white shadow-[0_8px_24px_-10px_rgba(79,63,224,.7)] brand-bg hover:brightness-110",
        secondary: "bg-surface-2 text-ink hover:bg-surface-3",
        outline: "border border-line bg-surface text-ink hover:bg-surface-2",
        ghost: "text-ink-2 hover:bg-surface-2 hover:text-ink",
        danger: "bg-bad-soft text-bad hover:brightness-95 dark:hover:brightness-125",
        "danger-solid": "bg-bad-solid text-white hover:brightness-110",
        success: "bg-ok-soft text-ok hover:brightness-95 dark:hover:brightness-125",
        link: "h-auto px-0 text-accent underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-7 px-2.5 text-[12px] rounded-lg [&_svg]:size-3.5",
        sm: "h-8 px-3 text-[13px] [&_svg]:size-4",
        md: "h-10 px-4 text-[14px] [&_svg]:size-4",
        lg: "h-12 px-5 text-[15px] rounded-2xl [&_svg]:size-5",
        icon: "size-9 p-0 [&_svg]:size-[18px]",
        "icon-sm": "size-8 p-0 rounded-lg [&_svg]:size-4",
        "icon-xs": "size-7 p-0 rounded-lg [&_svg]:size-3.5",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    icon?: ReactNode;
    iconEnd?: ReactNode;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, loading, icon, iconEnd, children, disabled, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : "button";
  const content = asChild ? (
    children
  ) : (
    <>
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : icon}
      {children}
      {iconEnd}
    </>
  );
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? "button")}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </Comp>
  );
});

/** An icon-only button: always has an accessible name (and a matching tooltip title). */
export const IconButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, "children" | "icon"> & { label: string; children: ReactNode }>(function IconButton(
  { label, children, size = "icon", variant = "ghost", ...props },
  ref,
) {
  return (
    <Button ref={ref} size={size} variant={variant} aria-label={label} title={label} {...props}>
      {children}
    </Button>
  );
});
